import type { StoplichtKleur, UserJourneyStatus } from '../types/klanttraject';

export function computeStoplicht(
  journey_status: UserJourneyStatus,
  last_activity_at: string | null,
  has_failed_or_pending_manual: boolean
): StoplichtKleur {
  if (journey_status === 'completed') return 'gray';
  if (journey_status === 'paused') return 'red';
  if (journey_status !== 'active') return 'gray';
  if (!last_activity_at) return 'green';

  const daysSince = (Date.now() - new Date(last_activity_at).getTime()) / (1000 * 60 * 60 * 24);
  if (daysSince > 7) return 'red';
  if (daysSince > 3 || has_failed_or_pending_manual) return 'yellow';
  return 'green';
}

export function stoplichtBg(kleur: StoplichtKleur): string {
  switch (kleur) {
    case 'green':  return 'bg-emerald-500';
    case 'yellow': return 'bg-amber-500';
    case 'red':    return 'bg-red-500';
    case 'gray':   return 'bg-slate-400';
  }
}

export function stoplichtTextColor(kleur: StoplichtKleur): string {
  switch (kleur) {
    case 'green':  return 'text-emerald-700';
    case 'yellow': return 'text-amber-700';
    case 'red':    return 'text-red-700';
    case 'gray':   return 'text-slate-600';
  }
}

export function formatScheduledFor(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffH = diffMs / (1000 * 60 * 60);
  if (diffH < -24) return 'achterstand';
  if (diffH < 0) return 'nu';
  if (diffH < 1) return `over ${Math.max(1, Math.round(diffH * 60))} min`;

  // Vergelijk op kalenderdag, niet op een 24u-venster: een stap om 09:00 morgen
  // is ~22u weg maar hoort "morgen" te heten, niet "vandaag".
  const time = d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(d) - startOfDay(now)) / (1000 * 60 * 60 * 24));
  if (dayDiff <= 0) return `vandaag ${time}`;
  if (dayDiff === 1) return `morgen ${time}`;
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) + ' ' + time;
}

export function formatDaysSilence(iso: string | null): string {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  if (days < 1) return 'vandaag actief';
  if (days === 1) return '1 dag stil';
  return `${days} dagen stil`;
}

export function statusLabel(status: string): string {
  return ({
    pending: 'In wachtrij',
    scheduled: 'Gepland',
    pending_manual: 'Wacht op support',
    completed: 'Voltooid',
    skipped: 'Overgeslagen',
    failed: 'Gefaald',
    paused: 'Gepauzeerd'
  } as Record<string, string>)[status] ?? status;
}

export function reasonLabel(reason: string | null): string {
  if (!reason) return '';
  return ({
    cron: 'cron',
    event: 'event',
    manual_admin: 'admin',
    manual_support: 'support',
    user_self: 'door user zelf',
    auto_timeout: 'auto-timeout'
  } as Record<string, string>)[reason] ?? reason;
}
